

"use client";
import {ReactNode, useState, useRef, useEffect} from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import clsx from "clsx";
interface ListProps {
    title: string;
    icon: ReactNode;
    value?: { adults: number; children: number; childAges: number[] };
    onChange?: (travelers: { adults: number; children: number; childAges: number[] }) => void;
}

import {DEFAULT_PAX_COUNT} from '@/constants/pricing'

export default function NumberofTravelers({title, icon, value, onChange}: ListProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [adults, setAdults] = useState(value?.adults ?? DEFAULT_PAX_COUNT);
    const [children, setChildren] = useState(value?.children ?? 0);
    const [childAges, setChildAges] = useState<number[]>(value?.childAges ?? []);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const onChangeRef = useRef(onChange);

    // onChange ref'ini güncelle
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // Dışarıya tıklandığında menüyü kapat
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // children değiştiğinde childAges array'ini senkronize et
    useEffect(() => {
        setChildAges((prevAges) => {
            if (children > prevAges.length) {
                // Çocuk sayısı artmışsa, eksik yaş alanlarını ekle
                const newAges = [...prevAges];
                while (newAges.length < children) {
                    newAges.push(0);
                }
                return newAges;
            } else if (children < prevAges.length) {
                // Çocuk sayısı azalmışsa, fazla yaş alanlarını kaldır
                return prevAges.slice(0, children);
            }
            return prevAges;
        });
    }, [children]);

    // value prop'u değiştiğinde state'i güncelle
    useEffect(() => {
        if (value) {
            setAdults(value.adults ?? DEFAULT_PAX_COUNT);
            setChildren(value.children ?? 0);
            setChildAges(value.childAges ?? []);
        }
    }, [value]);

    // onChange callback'i çağır (sonsuz döngüyü önlemek için ref kullan)
    useEffect(() => {
        if (onChangeRef.current) {
            onChangeRef.current({ adults, children, childAges });
        }
    }, [adults, children, childAges]);

    const handleAdultIncrease = () => {
        if (adults < 99) {
            setAdults(adults + 1);
        }
    };

    const handleAdultDecrease = () => {
        if (adults > 0) {
            setAdults(adults - 1);
        }
    };

    const handleChildrenIncrease = () => {
        if (children < 12) {
            setChildren(children + 1);
            setChildAges([...childAges, 0]);
        }
    };

    const handleChildrenDecrease = () => {
        if (children > 0) {
            setChildren(children - 1);
            setChildAges(childAges.slice(0, -1));
        }
    };

    const handleChildAgeChange = (index: number, age: number) => {
        const newAges = [...childAges];
        newAges[index] = age;
        setChildAges(newAges);
    };

    const totalTravelers = adults + children;

    return (
        <motion.div 
            ref={dropdownRef}
            className="w-full relative"
            variants={{hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 }}}
        >
            <label htmlFor="travelers-select" className="text-alsero-dark-gray text-base sm:text-lg mb-1">{title}</label>
            <button
                id="travelers-select"
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-label={`Select number of travelers. Currently: ${totalTravelers} ${totalTravelers === 1 ? 'traveler' : 'travelers'}`}
                className={clsx(
                    'relative flex flex-row justify-between items-center px-4 sm:px-6 py-3 bg-white rounded-lg w-full min-h-[50px] sm:min-h-[60px]',
                    'focus:outline-none cursor-pointer'
                )}
            >
                <div className="flex flex-row gap-2 items-center">
                    {icon}
                    <span className="text-alsero-dark-gray text-base sm:text-lg">{totalTravelers.toString().padStart(2, '0')}</span>
                </div>
                <svg 
                    width="15" 
                    height="8" 
                    viewBox="0 0 15 8" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    className={clsx('transition-transform duration-200', isOpen && 'rotate-180')}
                    aria-hidden="true"
                    focusable="false"
                >
                    <path fillRule="evenodd" clipRule="evenodd" d="M14.3145 0.454593C14.6735 0.796556 14.6735 1.35099 14.3145 1.69295L8.18677 7.53063C7.82782 7.87259 7.24584 7.87259 6.88688 7.53063L0.75913 1.69295C0.400175 1.35099 0.400175 0.796555 0.75913 0.454592C1.11808 0.112629 1.70007 0.112629 2.05902 0.454592C5.12634 3.37671 9.94731 3.37671 13.0146 0.454593C13.3736 0.112629 13.9556 0.112629 14.3145 0.454593Z" fill="#2E2D2D"/>
                </svg>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial="closed"
                        animate="open"
                        exit="closed"
                        variants={{
                            open: {
                                opacity: 1,
                                scale: 1,
                                y: 0,
                                transition: {
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 24,
                                    staggerChildren: 0.05,
                                    delayChildren: 0.1
                                }
                            },
                            closed: {
                                opacity: 0,
                                scale: 0.95,
                                y: -10,
                                transition: {
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 24
                                }
                            }
                        }}
                        className="absolute top-full left-0 right-0 sm:left-auto sm:right-0 sm:w-auto w-full mt-2 z-50 rounded-lg bg-white p-4 sm:p-6 shadow-lg flex flex-col"
                    >
                        <div className="flex flex-row gap-3 w-full justify-between items-center">
                            {/* Yetişkin Sayısı */}
                            <motion.div 
                                className="mb-3 sm:mb-4 w-full"
                                variants={{
                                    open: {
                                        opacity: 1,
                                        x: 0,
                                        transition: {
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 24
                                        }
                                    },
                                    closed: {
                                        opacity: 0,
                                        x: -10
                                    }
                                }}
                            >
                                <label htmlFor="adults-count" className="text-alsero-dark-gray text-sm sm:text-base font-medium mb-2 block">Yetişkin Sayısı</label>
                                <div className="flex items-center bg-alsero-soft-white/50 rounded-lg p-2">
                                    <button
                                        type="button"
                                        onClick={handleAdultDecrease}
                                        disabled={adults === 0}
                                        aria-label="Decrease adult count"
                                        className={clsx(
                                            'w-8 h-8 sm:w-10 sm:h-10 rounded-tl-lg rounded-bl-lg flex items-center justify-center text-white text-lg sm:text-xl font-semibold transition-colors cursor-pointer min-h-[44px] min-w-[44px]',
                                            adults === 0 ? 'bg-alsero-light-gray/40 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
                                        )}
                                    >
                                        −
                                    </button>
                                    <input
                                        id="adults-count"
                                        type="text"
                                        readOnly
                                        value={adults}
                                        aria-label={`Adult count: ${adults}`}
                                        className="text-center h-8 w-16 sm:h-10 sm:w-20 text-alsero-dark-gray text-base sm:text-lg font-medium bg-alsero-soft-white/60"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAdultIncrease}
                                        disabled={adults === 99}
                                        aria-label="Increase adult count"
                                        className={clsx(
                                            'w-8 h-8 sm:w-10 sm:h-10 rounded-tr-lg rounded-br-lg flex items-center justify-center text-white text-lg sm:text-xl font-semibold transition-colors cursor-pointer min-h-[44px] min-w-[44px]',
                                            adults === 99 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
                                        )}
                                    >
                                        +
                                    </button>
                                </div>
                            </motion.div>
                                            
                            {/* Çocuk Sayısı */}
                            <motion.div 
                                className="mb-3 sm:mb-4 w-full"
                                variants={{
                                    open: {
                                        opacity: 1,
                                        x: 0,
                                        transition: {
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 24
                                        }
                                    },
                                    closed: {
                                        opacity: 0,
                                        x: -10
                                    }
                                }}
                            >
                                <label htmlFor="children-count" className="text-alsero-dark-gray text-sm sm:text-base font-medium mb-2 block">Çocuk Sayısı</label>
                                <div className="flex items-center bg-alsero-soft-white/50 rounded-lg p-2">
                                    <button
                                        type="button"
                                        onClick={handleChildrenDecrease}
                                        disabled={children === 0}
                                        aria-label="Decrease children count"
                                        className={clsx(
                                            'w-8 h-8 sm:w-10 sm:h-10 rounded-tl-lg rounded-bl-lg flex items-center justify-center text-white text-lg sm:text-xl font-semibold transition-colors cursor-pointer min-h-[44px] min-w-[44px]',
                                            children === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
                                        )}
                                    >
                                        −
                                    </button>
                                    <input
                                        id="children-count"
                                        type="text"
                                        readOnly
                                        value={children}
                                        aria-label={`Children count: ${children}`}
                                        className="text-center h-8 w-16 sm:h-10 sm:w-20 text-alsero-dark-gray text-base sm:text-lg font-medium bg-alsero-soft-white/60"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleChildrenIncrease}
                                        disabled={children === 12}
                                        aria-label="Increase children count"
                                        className={clsx(
                                            'w-8 h-8 sm:w-10 sm:h-10 rounded-tr-lg rounded-br-lg flex items-center justify-center text-white text-lg sm:text-xl font-semibold transition-colors cursor-pointer min-h-[44px] min-w-[44px]',
                                            children === 12 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
                                        )}
                                    >
                                        +
                                    </button>
                                </div>
                            </motion.div>
                        </div>

                        {/* Çocuk Yaşları */}
                        {children > 0 && (
                            <div className="mt-4 pt-4 border-t border-alsero-soft-white">
                                <label htmlFor={`child-age-0`} className="text-alsero-dark-gray text-sm sm:text-base font-medium mb-3 flex items-center gap-2">
                                    Çocuk Yaşları
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-blue-500" aria-hidden="true" focusable="false">
                                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <path d="M8 4.5V5M8 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                                    <AnimatePresence mode="popLayout">
                                        {childAges.map((age, index) => (
                                            <motion.div 
                                                key={`child-age-${index}`}
                                                className="flex flex-col" 
                                                initial="closed"
                                                animate="open"
                                                exit="closed"
                                                variants={{
                                                    open: {
                                                        opacity: 1,
                                                        x: 0,
                                                        transition: {
                                                            type: "spring",
                                                            stiffness: 300,
                                                            damping: 24
                                                        }
                                                    },
                                                    closed: {
                                                        opacity: 0,
                                                        x: -10
                                                    }
                                                }}
                                            >
                                                <label htmlFor={`child-age-${index}`} className="sr-only">Child {index + 1} age</label>
                                                <input
                                                    id={`child-age-${index}`}
                                                    type="number"
                                                    value={age}
                                                    onChange={(e) => {
                                                        const value = parseInt(e.target.value) || 0;
                                                        if (value >= 0 && value <= 17) {
                                                            handleChildAgeChange(index, value);
                                                        }
                                                    }}
                                                    min="0"
                                                    max="17"
                                                    aria-label={`Child ${index + 1} age in years`}
                                                    className="w-full border border-alsero-soft-white rounded-lg p-2 text-center text-alsero-dark-gray focus:outline-none focus:border-blue-500 min-h-[44px]"
                                                    placeholder="Yaş"
                                                />
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}


